import React, { useState } from 'react'
import { NodeViewWrapper, NodeViewContent } from '@tiptap/react'
import { ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Input } from '@deepguide-ai/ui/input'

interface ToggleNodeViewProps {
  node: any
  updateAttributes: (attrs: any) => void
  selected: boolean
}

export const ToggleNodeView: React.FC<ToggleNodeViewProps> = ({
  node,
  updateAttributes,
  selected,
}) => {
  const [isExpanded, setIsExpanded] = useState(node.attrs.defaultOpen || false)
  const [isEditingTitle, setIsEditingTitle] = useState(false)
  const [title, setTitle] = useState(node.attrs.title || 'Toggle')

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTitle = e.target.value
    setTitle(newTitle)
    updateAttributes({ title: newTitle })
  }

  const handleTitleBlur = () => {
    setIsEditingTitle(false)
    if (!title.trim()) {
      setTitle('Toggle')
      updateAttributes({ title: 'Toggle' })
    }
  }

  const handleToggle = () => {
    setIsExpanded(!isExpanded)
  }

  return (
    <NodeViewWrapper 
      className={cn(
        'toggle-wrapper my-4',
        selected && 'ProseMirror-selectednode'
      )}
    >
      <div className="toggle-container">
        <div 
          className="toggle-header flex items-center gap-2 px-3 py-2 rounded-md hover:bg-accent cursor-pointer"
          onClick={handleToggle}
        >
          <ChevronRight
            className={cn(
              'h-4 w-4 flex-shrink-0 transition-transform duration-200',
              isExpanded && 'rotate-90'
            )}
          />
          {isEditingTitle ? (
            <Input
              value={title}
              onChange={handleTitleChange}
              onBlur={handleTitleBlur}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  handleTitleBlur()
                }
                if (e.key === 'Escape') {
                  e.preventDefault()
                  setTitle(node.attrs.title || 'Toggle')
                  setIsEditingTitle(false)
                }
              }}
              onClick={(e) => e.stopPropagation()}
              className="flex-1 h-auto py-0 px-1 text-sm font-medium border-none focus:ring-0"
              autoFocus
            />
          ) : (
            <span 
              className="flex-1 font-medium select-none"
              onDoubleClick={(e) => {
                e.stopPropagation()
                setIsEditingTitle(true)
              }}
            >
              {title}
            </span>
          )}
        </div>
        
        <div
          className={cn(
            'toggle-content overflow-hidden transition-all duration-300 ease-in-out',
            isExpanded ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'
          )}
        >
          <div className="pl-9 pr-3 py-2">
            <NodeViewContent />
          </div>
        </div>
      </div>
    </NodeViewWrapper>
  )
}